import mapValues from "lodash/mapValues";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import { DEFAULT_FLOATING_POSITION } from "../constants";
import type { BubbleAxisAnchor, BubblePosition } from "../types";

export type PanelSize = { width: number; height: number };

export type FloatingEntry = {
  position: BubblePosition;
  isOpen: boolean;
  size?: PanelSize;
};

type State = {
  entries: Record<string, FloatingEntry>;
};

type Actions = {
  setPosition: (storageKey: string, position: BubblePosition) => void;
  setOpen: (storageKey: string, isOpen: boolean) => void;
  setSize: (storageKey: string, size: PanelSize) => void;
  resetEntry: (storageKey: string) => void;
};

type FloatingPositionStore = State & { actions: Actions };

const DEFAULT_ENTRY: FloatingEntry = { position: DEFAULT_FLOATING_POSITION, isOpen: false };

const isValidAxis = (axis: unknown): axis is BubbleAxisAnchor => {
  if (typeof axis !== "object" || axis === null) {
    return false;
  }
  const candidate = axis as { type?: unknown; offset?: unknown; value?: unknown };
  if (candidate.type === "start" || candidate.type === "end") {
    return typeof candidate.offset === "number";
  }
  if (candidate.type === "fraction") {
    return typeof candidate.value === "number" && candidate.value >= 0 && candidate.value <= 1;
  }
  return false;
};

const isValidPosition = (position: unknown): position is BubblePosition => {
  if (typeof position !== "object" || position === null) {
    return false;
  }
  const candidate = position as { horizontal?: unknown; vertical?: unknown };
  return isValidAxis(candidate.horizontal) && isValidAxis(candidate.vertical);
};

const isValidSize = (size: unknown): size is PanelSize => {
  if (typeof size !== "object" || size === null) {
    return false;
  }
  const candidate = size as { width?: unknown; height?: unknown };
  return typeof candidate.width === "number" && typeof candidate.height === "number";
};

const PERSIST_CONFIG = {
  name: "floating_expandable_bubble_positions",
  version: 1,
  partialize: (state: FloatingPositionStore) => ({
    entries: Object.fromEntries(
      Object.entries(state.entries).map(([key, entry]) => [
        key,
        { position: entry.position, ...(entry.size !== undefined ? { size: entry.size } : {}) }
      ])
    )
  }),
  merge: (persisted: unknown, current: FloatingPositionStore): FloatingPositionStore => {
    const persistedEntries = (persisted as State | undefined)?.entries ?? {};
    const entries = mapValues(persistedEntries, (value): FloatingEntry => {
      const candidate =
        typeof value === "object" && value !== null ? (value as { position?: unknown; size?: unknown }) : {};
      const position = isValidPosition(candidate.position)
        ? (candidate.position as BubblePosition)
        : DEFAULT_FLOATING_POSITION;
      const size = isValidSize(candidate.size) ? (candidate.size as PanelSize) : undefined;
      return { position, isOpen: false, ...(size !== undefined ? { size } : {}) };
    });
    return { ...current, entries };
  }
};

const useFloatingPositionStore = create<FloatingPositionStore>()(
  persist(
    (set) => ({
      entries: {},
      actions: {
        setPosition: (storageKey, position) =>
          set((state) => ({
            entries: {
              ...state.entries,
              [storageKey]: { ...(state.entries[storageKey] ?? DEFAULT_ENTRY), position }
            }
          })),
        setOpen: (storageKey, isOpen) =>
          set((state) => ({
            entries: {
              ...state.entries,
              [storageKey]: { ...(state.entries[storageKey] ?? DEFAULT_ENTRY), isOpen }
            }
          })),
        setSize: (storageKey, size) =>
          set((state) => ({
            entries: {
              ...state.entries,
              [storageKey]: { ...(state.entries[storageKey] ?? DEFAULT_ENTRY), size }
            }
          })),
        resetEntry: (storageKey) =>
          set((state) => ({
            entries: {
              ...state.entries,
              [storageKey]: { ...DEFAULT_ENTRY }
            }
          }))
      }
    }),
    PERSIST_CONFIG
  )
);

export const useFloatingEntry = (storageKey: string): FloatingEntry =>
  useFloatingPositionStore((state) => state.entries[storageKey] ?? DEFAULT_ENTRY);

export const useFloatingPositionStoreActions = () =>
  useFloatingPositionStore(useShallow((state) => state.actions));
